import './FilterOptionSecondLevelCollections.scss';

import ArrowButton from '@components/ArrowButton';
import type { FilterOptionItemSettings } from '@components/FilterOptionItem';
import { FilterOptionItem } from '@components/FilterOptionItem';
import FilterOptionItemButton from '@components/FilterOptionItem/Button';
import FilterOptionItemLabel from '@components/FilterOptionItem/Label';
import type {
  FilterOptionCollectionItemHookProps,
  FilterOptionCollectionItemProps,
} from '@components/FilterOptionMultiLevelCollections';
import { createClsNameMap, stripHtml } from '@utils';

import FilterOptionThirdLevelCollections from '../FilterOptionThirdLevelCollections';

export const clsNameMap = createClsNameMap({
  elements: {
    title: createClsNameMap(),
  },
})('filter-option-item-multilevel-collections-secondlevel');

export type FilterOptionSecondLevelCollectionsItemProps = {
  collectionId: string;
  collectionHandle: string;
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  value: {
    tag: string;
    displayName: string;
    subTags?: Array<{
      tag: string;
      displayName: string;
    }>;
    settings: FilterOptionItemSettings;
  };
};

export type FilterOptionSecondLevelCollectionsProps = {
  collectionId: string;
  collectionHandle: string;
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  options: Array<FilterOptionSecondLevelCollectionsItemProps['value']>;
};

const FilterOptionSecondLevelCollectionsItem = ({
  collectionHandle,
  value: { displayName, tag, subTags, settings },
  collectionId,
  useFilterOptionCollectionItem,
}: FilterOptionSecondLevelCollectionsItemProps) => {
  const {
    collectionUrlPath,
    isOptionItemSelected,
    isChildListExpanded,
    toggleChildListExpandedState,
    handleSelectOptionItem,
  } = useFilterOptionCollectionItem({
    collectionId,
    collectionHandle,
    tag,
    subTags,
    hasChildListFilterOptionItem: !!subTags?.length,
  });

  if (!stripHtml(displayName)) return null;

  return (
    <>
      <FilterOptionItem as='div' className={clsNameMap.elm('title')}>
        <FilterOptionItemButton
          as='link'
          isSelected={isOptionItemSelected}
          label={<FilterOptionItemLabel settings={settings} label={displayName} />}
          link={collectionUrlPath}
          onClick={(e: Event) => handleSelectOptionItem(e)}
        />
        {!!subTags?.length && (
          <ArrowButton
            label={`Expand/Collapse ${displayName}`}
            modifiers={isChildListExpanded ? ['bottom'] : ['right']}
            onClick={toggleChildListExpandedState}
            isFilter={true}
          />
        )}
      </FilterOptionItem>
      {!!subTags?.length && isChildListExpanded && (
        <FilterOptionThirdLevelCollections
          useFilterOptionCollectionItem={useFilterOptionCollectionItem}
          collectionId={collectionId}
          collectionHandle={collectionHandle}
          options={subTags.map((item) => ({
            ...item,
            settings,
          }))}
        />
      )}
    </>
  );
};

const FilterOptionSecondLevelCollections = ({
  options,
  collectionId,
  collectionHandle,
  useFilterOptionCollectionItem,
}: FilterOptionSecondLevelCollectionsProps) => {
  return (
    <>
      {options.map((option, i) => (
        <li key={i} className={clsNameMap.root()}>
          <FilterOptionSecondLevelCollectionsItem
            key={option.tag}
            collectionId={collectionId}
            collectionHandle={collectionHandle}
            value={option}
            useFilterOptionCollectionItem={useFilterOptionCollectionItem}
          />
        </li>
      ))}
    </>
  );
};

export default FilterOptionSecondLevelCollections;
